/* Public careers page — UNAUTHENTICATED. Lists open job profiles and accepts applications.
   Lives at /careers — does NOT use AppLayout / AuthProvider. */
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Briefcase, MapPin, Building2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/careers`;

export default function Careers() {
  const [info, setInfo] = useState(null);
  const [openings, setOpenings] = useState([]);
  const [applyTo, setApplyTo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/info`).then((r) => setInfo(r.data)).catch(() => {});
    axios.get(`${API}/openings`).then((r) => setOpenings(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="careers-page">
      {/* Public header */}
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          {info?.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${info.logo_url}`} alt="" className="size-8 object-contain" />}
          <div>
            <div className="display font-black text-lg tracking-tight">{info?.company_name || "Careers"}</div>
            {info?.tagline && <div className="text-[11px] text-muted-foreground -mt-0.5">{info.tagline}</div>}
          </div>
          <div className="ml-auto text-xs text-muted-foreground">{info?.website}</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Briefcase className="size-3.5" /> Open roles</div>
          <h1 className="display text-3xl md:text-5xl font-black tracking-tighter mt-1">Build your career with us</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-base max-w-2xl">We're hiring across multiple disciplines. Pick the role that fits, send us your resume, and we'll be in touch within a week.</p>
        </div>

        {openings.length === 0 ? (
          <Card className="border-dashed border-border">
            <CardContent className="p-12 text-center">
              <Briefcase className="size-12 text-muted-foreground mx-auto opacity-50" />
              <div className="display font-bold text-lg mt-3">No open positions right now</div>
              <div className="text-sm text-muted-foreground">Check back soon — or email us at {info?.email || "us"} to introduce yourself.</div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="careers-openings">
            {openings.map((j) => (
              <Card key={j.id} className="border-border hover:shadow-lg transition-shadow" data-testid={`careers-job-${j.id}`}>
                <CardContent className="p-5">
                  <div className="display font-bold text-lg">{j.title}</div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
                    {j.department && <span className="inline-flex items-center gap-1"><Building2 className="size-3" /> {j.department}</span>}
                    {j.location && <span className="inline-flex items-center gap-1"><MapPin className="size-3" /> {j.location}</span>}
                    {j.employment_type && <span className="chip bg-secondary text-foreground text-[10px]">{j.employment_type}</span>}
                    {j.seniority && <span className="chip bg-secondary text-foreground text-[10px]">{j.seniority}</span>}
                  </div>
                  {j.role_summary && <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{j.role_summary}</p>}
                  {j.required_skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {j.required_skills.slice(0, 6).map((s) => <span key={s} className="chip bg-primary/10 text-primary border-primary/30 text-[10px]">{s}</span>)}
                    </div>
                  )}
                  <Button onClick={() => setApplyTo(j)} className="gap-2 mt-4 w-full" data-testid={`careers-apply-${j.id}`}><Send className="size-4" /> Apply now</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-border mt-12">
        <div className="max-w-6xl mx-auto px-6 py-6 text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} {info?.company_name || "Company"} · All rights reserved.</span>
          {info?.email && <a href={`mailto:${info.email}`} className="hover:text-foreground">{info.email}</a>}
        </div>
      </footer>

      {applyTo && <ApplyDialog job={applyTo} onClose={() => setApplyTo(null)} />}
    </div>
  );
}

function ApplyDialog({ job, onClose }) {
  const [p, setP] = useState({ name: "", email: "", phone: "", location: "", cover_letter: "" });
  const [file, setFile] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!p.name || !p.email) { toast.error("Name + email required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(p).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await axios.post(`${API}/apply/${job.id}`, fd);
      setDone(true);
      toast.success("Application received");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="careers-apply-dialog">
        <DialogHeader><DialogTitle>{done ? "Application received" : `Apply · ${job.title}`}</DialogTitle></DialogHeader>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="size-12 text-emerald-500 mx-auto" />
            <div className="display font-bold text-lg mt-3">Thank you, {p.name.split(" ")[0]}!</div>
            <p className="text-sm text-muted-foreground mt-1">We've received your application for <strong>{job.title}</strong>. Our team will review it and reach out within 5–7 business days.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <Input placeholder="Full name *" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="apply-name" />
              <Input type="email" placeholder="Email *" value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} data-testid="apply-email" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Phone" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} />
                <Input placeholder="Location" value={p.location} onChange={(e) => setP({ ...p, location: e.target.value })} />
              </div>
              <Textarea rows={4} placeholder="Why are you a great fit? (optional)" value={p.cover_letter} onChange={(e) => setP({ ...p, cover_letter: e.target.value })} />
              <div>
                <div className="text-xs font-semibold mb-1">Resume (PDF / DOCX) — optional</div>
                <input type="file" accept=".pdf,.docx,.doc" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="apply-file" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={busy} className="gap-2" data-testid="apply-submit"><Send className="size-4" /> {busy ? "Submitting…" : "Submit application"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
